import { Text } from "~/components/Text"
import { VideoPlayer } from "~/components/VideoPlayer"
import {
  AudioOutlined,
  ExclamationCircleFilled,
  FileImageOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons"
import { NodeViewWrapper, type ReactNodeViewProps } from "@tiptap/react"
import { useRef, useState } from "react"
import { Dropdown, Flex, Image, type MenuProps } from "antd"
import classNames from "classnames"
import type { MentionResource } from "~/common/media/resourceMentions"
import { ImageThumbnail } from "~/components/ImageThumbnail"
import styles from "./index.css"
import type { MentionNodeAttrs, MentionTokenClickHandler } from "./types"

type MentionTokenProps = ReactNodeViewProps & {
  onMentionClick: MentionTokenClickHandler
}

export const MentionToken = ({
  onMentionClick,
  ...props
}: MentionTokenProps) => {
  const attrs = props.node.attrs as MentionNodeAttrs
  const missingMentionRef = useRef<HTMLButtonElement | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const isImage = attrs.kind === "image"
  const isVideo = attrs.kind === "video"
  const resourceKindLabel = isImage ? "图片" : isVideo ? "视频" : "音频"
  const visibleMentionLabel = attrs.missing
    ? `失效${resourceKindLabel}`
    : `@${attrs.label}`
  const missingMentionMenuItems: MenuProps["items"] = [
    {
      key: "replace",
      label: `链接缺失，替换${resourceKindLabel}`,
    },
    {
      type: "divider",
    },
    {
      key: "delete",
      label: "删除文本",
    },
  ]

  const openResourceMenu = (anchorElement: HTMLElement) => {
    const position = props.getPos()

    if (!props.editor.isEditable || typeof position !== "number") return

    onMentionClick({
      anchorElement,
      editor: props.editor,
      mentionId: attrs.id,
      position,
    })
  }

  const replaceMissingMention = () => {
    const anchorElement = missingMentionRef.current

    if (!anchorElement) return

    openResourceMenu(anchorElement)
  }

  const deleteMissingMention = () => {
    props.deleteNode()
    props.editor.commands.focus()
  }

  const mentionThumb = isImage ? (
    attrs.url ? (
      <img
        className={styles.mentionThumb}
        src={attrs.url}
        alt={attrs.name || attrs.label}
      />
    ) : (
      <span className={styles.mentionThumbIcon}>
        <FileImageOutlined />
      </span>
    )
  ) : isVideo ? (
    attrs.url ? (
      <VideoPlayer
        aria-hidden
        className={styles.mentionThumb}
        muted
        preload="metadata"
        src={attrs.url}
      />
    ) : (
      <span className={styles.mentionThumbIcon}>
        <VideoCameraOutlined />
      </span>
    )
  ) : (
    <span className={styles.mentionThumbIcon}>
      <AudioOutlined />
    </span>
  )

  const mentionContent = (
    <>
      {mentionThumb}
      <Text
        size={13}
        className={styles.mentionText}
      >
        {visibleMentionLabel}
      </Text>
    </>
  )

  if (attrs.missing && props.editor.isEditable) {
    return (
      <NodeViewWrapper
        as="span"
        className={styles.mentionNode}
      >
        <Dropdown
          classNames={{ root: styles.missingMentionDropdown }}
          menu={{
            items: missingMentionMenuItems,
            onClick: ({ key }) => {
              switch (key) {
                case "replace":
                  replaceMissingMention()
                  return
                case "delete":
                  deleteMissingMention()
                  break
              }
            },
          }}
          placement="bottom"
          trigger={["click"]}
        >
          <button
            ref={missingMentionRef}
            aria-label={`失效${resourceKindLabel}`}
            aria-haspopup="menu"
            className={classNames(
              styles.mentionToken,
              styles.mentionTokenMissing,
            )}
            contentEditable={false}
            data-missing
            type="button"
            onMouseDown={(event) => {
              event.stopPropagation()
            }}
          >
            {mentionContent}
          </button>
        </Dropdown>
      </NodeViewWrapper>
    )
  }

  return (
    <NodeViewWrapper
      as="span"
      className={styles.mentionNode}
    >
      {props.editor.isEditable ? (
        <button
          aria-label={`替换引用 @${attrs.label}`}
          className={styles.mentionToken}
          contentEditable={false}
          type="button"
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            openResourceMenu(event.currentTarget)
          }}
          onMouseDown={(event) => {
            event.stopPropagation()
          }}
        >
          {mentionContent}
        </button>
      ) : isImage && attrs.url && !attrs.missing ? (
        <Image.PreviewGroup
          items={[attrs.url]}
          preview={{ open: previewOpen, onOpenChange: setPreviewOpen }}
        >
          <button
            aria-label={`预览引用 @${attrs.label}`}
            className={styles.mentionToken}
            type="button"
            onClick={() => setPreviewOpen(true)}
          >
            {mentionContent}
          </button>
        </Image.PreviewGroup>
      ) : (
        <span
          className={classNames(
            styles.mentionToken,
            attrs.missing && styles.mentionTokenMissing,
          )}
        >
          {mentionContent}
        </span>
      )}
    </NodeViewWrapper>
  )
}

export const renderMentionOption = (item: MentionResource, active: boolean) => {
  return (
    <div
      className={classNames(styles.menuItem, active && styles.menuItemActive)}
    >
      <div className={styles.menuThumb}>
        {item.kind === "image" ? (
          <ImageThumbnail
            alt={item.label}
            className={styles.menuThumbImageFrame}
            preview={false}
            size="small"
            src={item.url}
          />
        ) : item.kind === "video" ? (
          <VideoPlayer
            aria-hidden
            className={styles.menuThumbVideo}
            muted
            preload="metadata"
            src={item.url}
          />
        ) : (
          <AudioOutlined />
        )}
      </div>
      <Flex
        className={styles.menuMeta}
        gap={2}
        vertical
      >
        <Text strong>@{item.label}</Text>
        <Text type="secondary">{item.name || "未命名资源"}</Text>
      </Flex>
      {item.kind === "image" ? (
        <FileImageOutlined className={styles.menuKind} />
      ) : item.kind === "video" ? (
        <VideoCameraOutlined className={styles.menuKind} />
      ) : (
        <AudioOutlined className={styles.menuKind} />
      )}
    </div>
  )
}
